import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Owns the microphone permission state machine, the single-track
// MediaRecorder, and the running timer. Does NOT touch the multitrack
// engine - the stream it acquires is exposed via streamRef so the
// engine and the overdub recorder can borrow it in later commits.
//
// Permission state values:
//   idle        - never asked yet
//   requesting  - getUserMedia in flight
//   granted     - stream is live and stored in streamRef
//   denied      - user said no
//   error       - any other failure (device busy, not found, etc.)
//
// The dual try/catch in requestAccess() is load-bearing on Safari and
// older Android Chrome: the rich constraints (sampleRate, channelCount)
// fail on some hardware, so we retry with basic constraints before
// telling the user.

export const useMicCapture = () => {
  const { t } = useTranslation();

  const [micPermission, setMicPermission] = useState('idle');
  const [micError, setMicError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);

  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const requestAccess = useCallback(async () => {
    setMicError(null);
    setMicPermission('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      setMicPermission('granted');
      toast.success(t('recorder.micEnabled'));
      return stream;
    } catch (error) {
      // Fallback: drop the sampleRate / channelCount constraints. Some
      // hardware (older Android Chrome, certain Bluetooth mics on
      // Safari) reject those.
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        streamRef.current = fallbackStream;
        setMicPermission('granted');
        toast.success(t('recorder.micEnabled'));
        return fallbackStream;
      } catch (_) {
        // both attempts failed; map the original error and surface it
      }

      let errorMessage = t('recorder.micErrorGeneric');
      switch (error.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
          errorMessage = t('recorder.micErrorPermission');
          setMicPermission('denied');
          break;
        case 'NotFoundError':
        case 'DevicesNotFoundError':
          errorMessage = t('recorder.micErrorNotFound');
          setMicPermission('error');
          break;
        case 'NotReadableError':
        case 'TrackStartError':
          errorMessage = t('recorder.micErrorBusy');
          setMicPermission('error');
          break;
        case 'OverconstrainedError':
          errorMessage = t('recorder.micErrorConstraints');
          setMicPermission('error');
          break;
        default:
          errorMessage = `${t('recorder.micErrorGeneric')}: ${error.message || error.name}`;
          setMicPermission('error');
      }
      setMicError(errorMessage);
      toast.error(errorMessage);
      return null;
    }
  }, [t]);

  const startRecording = useCallback(async () => {
    if (micPermission !== 'granted' || !streamRef.current || !streamRef.current.active) {
      const s = await requestAccess();
      if (!s) return false;
    }

    try {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const actualMimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setAudioBlob(blob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      return true;
    } catch (error) {
      console.error('startRecording error', error);
      toast.error(t('recorder.startError'));
      return false;
    }
  }, [micPermission, requestAccess, t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  const discardCurrent = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setAudioBlob(null);
    setRecordingTime(0);
  }, [audioUrl]);

  // Cleanup on unmount: stop the stream tracks and clear the timer.
  // The audioUrl revoke is handled via discardCurrent / page exit;
  // since the URL is also written into <audio src>, eager revocation
  // here could break a clip that's still playing.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach(tr => tr.stop());
        } catch (_) {}
        streamRef.current = null;
      }
    };
  }, []);

  return {
    // State
    micPermission,
    micError,
    isRecording,
    audioUrl,
    audioBlob,
    recordingTime,

    // Shared refs (needed by the multitrack engine + overdub recorder
    // in the next commits)
    streamRef,
    mediaRecorderRef,

    // Actions
    requestAccess,
    startRecording,
    stopRecording,
    discardCurrent,
  };
};

export default useMicCapture;
